import axios from "axios";
import moment from "moment";

import config from "../config/config";
import type { AppState } from "@/reducers";
import type { AppDispatch, AppThunk } from "./index";
import {
  APP_LOADING_END,
  APP_LOADING_START,
  APP_DISPLAY_SETTING_SET,
  APP_ADVERTISEMENTS_SET,
  APP_UPDATE_AVAILABLE,
  APP_SOCIAL_MEDIA_ITEMS_SET,
  GAME_CONTEST_SET,
  GAME_CONTEST_CREDITS_SET,
  LOYALTY_PROGRAM_SET,
} from "./types";
import { apollo } from "./index";
import { gql } from "@apollo/client";
import CookieManager from "@/services/CookieManager";

export const checkAppVersion =
  (): AppThunk => async (dispatch: AppDispatch, getState: () => AppState) => {
    try {
      if (config.checkAppVersion) {
        const { app } = getState();

        if (app && app.updateAvailable === false) {
          const req = await axios.get(`/version.txt?ts=${moment().unix()}`);

          if (Number(req.data) > Number(config.release)) {
            dispatch({ type: APP_UPDATE_AVAILABLE });
          }
        }
      }
    } catch {
      // nop
    }
  };

export const getAppDisplaySetting =
  (): any => async (dispatch: AppDispatch, getState: () => AppState) => {
    try {
      dispatch({ type: APP_LOADING_START });
      const { account } = getState();
      if (account && account?.profile?.companyEntity?.id) {
        await apollo().cache.reset();
        const req = await apollo().query({
          variables: {
            entityId: account?.profile?.companyEntity?.id,
          },
          query: gql`
            query getAppDisplaySetting($entityId: String!) {
              appDisplaySettingAtDate(entityId: $entityId) {
                homeBackgroundImage
              }
            }
          `,
        });

        if (req.data) {
          dispatch({
            type: APP_DISPLAY_SETTING_SET,
            appDisplaySetting: req.data.appDisplaySettingAtDate,
          });
        }
      }
      dispatch({ type: APP_LOADING_END });
    } catch (e) {
      // nop
      dispatch({ type: APP_LOADING_END });
    }
  };

export const getAppAdvertisements =
  (): any => async (dispatch: AppDispatch, getState: () => AppState) => {
    try {
      dispatch({ type: APP_LOADING_START });
      const { account } = getState();
      if (account && account?.profile?.companyEntity?.id) {
        await apollo().cache.reset();
        const req = await apollo().query({
          variables: {
            entityId: account?.profile?.companyEntity?.id,
          },
          query: gql`
            query getAppAdvertisements($entityId: String!) {
              appAdvertisementsAtDate(entityId: $entityId) {
                id
                frequency
                pictures
                link
                type
              }
            }
          `,
        });

        if (req.data) {
          const appAdvertisements = req.data.appAdvertisementsAtDate ?? [];
          const popupAdvertisement =
            appAdvertisements.find((advertisement: any) => advertisement.type === "popup") ?? null;
          const homeCardAdvertisement =
            appAdvertisements.find((advertisement: any) => advertisement.type === "homeCard") ??
            null;

          let popupToDisplay = popupAdvertisement;
          if (popupAdvertisement) {
            const lastTimePopupShown = await CookieManager.get(config.cookies.url)
              .then((cookies: any) => {
                return cookies[`popup_${popupAdvertisement.id}`]?.value as string | undefined;
              })
              .catch(() => false);

            if (lastTimePopupShown && popupAdvertisement.frequency === "once") {
              popupToDisplay = null;
            } else if (
              lastTimePopupShown &&
              moment(lastTimePopupShown as string).isAfter(moment().subtract(1, "days"))
            ) {
              popupToDisplay = null;
            }

            if (popupToDisplay) {
              await CookieManager.set(config.cookies.url, {
                name: `popup_${popupAdvertisement.id}`,
                value: moment().format(),
              });
            }
          }

          dispatch({
            type: APP_ADVERTISEMENTS_SET,
            appAdvertisements: {
              popup: popupToDisplay,
              homeCard: homeCardAdvertisement,
            },
          });
        } else {
          dispatch({
            type: APP_ADVERTISEMENTS_SET,
            appAdvertisements: {
              popup: null,
              homeCard: null,
            },
          });
        }
      }
      dispatch({ type: APP_LOADING_END });
    } catch (e) {
      // nop
      dispatch({ type: APP_LOADING_END });
    }
  };

const getGameContestCredits = async (gameContestId: number) => {
  try {
    const req = await apollo().query({
      variables: {
        gameContestId,
      },
      query: gql`
        query gameContestCreditsSearch($gameContestId: Float!) {
          gameContestCreditsSearch(gameContestId: $gameContestId) {
            id
            hasBeenUsed
            isWinningCredit
          }
        }
      `,
    });
    return req.data?.gameContestCreditsSearch || [];
  } catch {
    return [];
  }
};

export const getGameContest =
  (): any => async (dispatch: AppDispatch, getState: () => AppState) => {
    try {
      dispatch({ type: APP_LOADING_START });
      await apollo().cache.reset();
      const { account } = getState();
      if (account && account?.profile?.companyEntity?.id) {
        const req = await apollo().query({
          variables: {
            entityId: account?.profile?.companyEntity?.id,
          },
          query: gql`
            query getGameContest($entityId: String!) {
              gameContestAtDate(entityId: $entityId) {
                id
                name
                type
                modalityTitle
                modalityDescription
                scratchScreenText
                winningScratchScreenText
                loosingScratchScreenText
                noCreditScreenText
                rewardImage
                bannerText
                bannerBackgroundColor
                gameScreenBackgroundColor
                wheelSegmentColors
                winningConfettiColors
                foregroundScratchImage
                winningBackgroundScratchImage
                loosingBackgroundScratchImage
                noCreditImage
                startAt
                endAt
                createdAt
              }
            }
          `,
        });

        if (req?.data?.gameContestAtDate) {
          dispatch({
            type: GAME_CONTEST_SET,
            gameContest: req.data.gameContestAtDate,
          });
          if (req.data.gameContestAtDate?.id) {
            dispatch({
              type: GAME_CONTEST_CREDITS_SET,
              gameContestCredits: await getGameContestCredits(req.data.gameContestAtDate.id),
            });
          }
        } else {
          dispatch({
            type: GAME_CONTEST_SET,
            gameContest: null,
          });
        }
      }
      dispatch({ type: APP_LOADING_END });
    } catch (e) {
      // nop
      dispatch({ type: APP_LOADING_END });
    }
  };

export const setGameContestCreditScratched =
  (gameContestCreditId: number): any =>
  async (dispatch: AppDispatch, getState: () => AppState) => {
    try {
      dispatch({ type: APP_LOADING_START });
      const req = await apollo().mutate({
        variables: {
          gameContestCreditId,
        },
        mutation: gql`
          mutation useGameContestCredit($gameContestCreditId: Float!) {
            useGameContestCredit(gameContestCreditId: $gameContestCreditId) {
              id
              hasBeenUsed
              isWinningCredit
            }
          }
        `,
      });

      if (req?.data?.useGameContestCredit) {
        dispatch({
          type: GAME_CONTEST_CREDITS_SET,
          gameContestCredits: req?.data?.useGameContestCredit,
        });
      }
      dispatch({ type: APP_LOADING_END });
    } catch (e) {
      // nop
      console.error(e);
      dispatch({ type: APP_LOADING_END });
    }
  };

export const getSocialMediaImages =
  (): any => async (dispatch: AppDispatch, getState: () => AppState) => {
    try {
      dispatch({ type: APP_LOADING_START });

      const req = await apollo().query({
        query: gql`
          query instagramPosts {
            instagramPosts {
              id
              shortcode
              media_url
              thumbnail_url
            }
          }
        `,
      });

      if (req.data?.instagramPosts) {
        dispatch({
          type: APP_SOCIAL_MEDIA_ITEMS_SET,
          socialMediaItems: req.data?.instagramPosts,
        });
      }
      dispatch({ type: APP_LOADING_END });
    } catch {
      // nop
      dispatch({ type: APP_LOADING_END });
    }
  };

export const getLoyaltyProgram =
  (): any => async (dispatch: AppDispatch, getState: () => AppState) => {
    try {
      dispatch({ type: APP_LOADING_START });
      const { account } = getState();
      if (account && account?.profile?.companyEntity?.id) {
        await apollo().cache.reset();
        const req = await apollo().query({
          variables: {
            entityId: account?.profile?.companyEntity?.id,
          },
          query: gql`
            query loyaltyProgram($entityId: String!) {
              loyaltyProgram(entityId: $entityId) {
                id
                name
                description
                levels {
                  id
                  name
                  description
                  image
                  threshold
                  credit
                }
              }
            }
          `,
        });

        if (req.data) {
          dispatch({
            type: LOYALTY_PROGRAM_SET,
            loyaltyProgram: req.data.loyaltyProgram,
          });
        }
      }
      dispatch({ type: APP_LOADING_END });
    } catch (e) {
      // nop
      dispatch({ type: APP_LOADING_END });
    }
  };

export const setLoadingStart =
  (message: string): AppThunk =>
  (dispatch: AppDispatch) => {
    dispatch({ type: APP_LOADING_START, message });
  };

export const setLoadingEnd = (): AppThunk => (dispatch: AppDispatch) => {
  dispatch({ type: APP_LOADING_END });
};
